import React, { PropsWithChildren, RefObject, useEffect } from 'react';
import tippy from 'tippy.js';
import { TooltipPlacement } from './Tooltip.enums';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { KeyEvent } from '../../../types/KeyEvent';
import { windowLogger } from '../../../_lib/Logger/windowLogger';

type TooltipProps = {
  attachTo: RefObject<HTMLElement | HTMLAnchorElement | null>;
  placement?: TooltipPlacement;
  hideTooltipFromScreenReader?: boolean;
  hideOnClick?: boolean;
};

/**
 * Tooltips based on https://github.com/atomiks/tippyjs
 *
 * This can be an useEffect in the future, when all components using this are functional.
 */

export function Tooltip({
  attachTo,
  hideOnClick = true,
  placement = TooltipPlacement.AUTO,
  hideTooltipFromScreenReader = false,
  children,
}: PropsWithChildren<TooltipProps>) {
  useEffect(() => {
    if (attachTo.current && attachTo.current.title) {
      try {
        const attachToElement = attachTo.current;
        const instance = tippy(attachToElement, {
          aria: hideTooltipFromScreenReader
            ? {
                // disable completely, left up to you to control
                content: null,
                expanded: undefined,
              }
            : {
                content: 'describedby', // `null` when interactive: true, otherwise "describedby"
                expanded: undefined,
              },
          arrow: false,
          // interactive: true appends the tooltip as sibling by default. But
          // it needs to be the last elem of the body
          appendTo: document.body,
          ignoreAttributes: true,
          placement,
          hideOnClick,
          duration: 0,
          delay: [400, 1], // minimum of 1 please! otherwise itp crashes the modal dialog in some cases
          interactive: true,
          interactiveBorder: 2,
          content: () => {
            // grab title and remove it from the element to prevent the browser from rendering the default tooltip
            const { title } = attachToElement;
            attachToElement.removeAttribute('title');
            return title as string;
          },
          onShow: (self) => {
            // update content of tooltip if title on the element changed. null check required here.
            if (isNotNullOrUndefined(attachToElement.title) && attachToElement.title !== self.props.content) {
              self.setContent(attachToElement.title);
              attachToElement.removeAttribute('title');
            }
          },
          // we specify different maxWidth for mobile via CSS
          maxWidth: 'none',
          // we use the title attribute to generate the tooltip content
          // https://atomiks.github.io/tippyjs/faq/#can-i-use-the-title-attribute
          offset: [0, 0],
          // if we set role to undefined, tippy will set it to 'tooltip' by default.
          // we want to disable this attribute sometimes, so we set it to empty string, since null is not allowed
          role: hideTooltipFromScreenReader ? '' : 'tooltip',
          popperOptions: {
            modifiers: [
              { name: 'preventOverflow', enabled: false },
              { name: 'hide', enabled: false },
            ],
          },
        });
        const onKeyDown = (e: Event) => {
          const event = e as KeyboardEvent;
          if (event.code === KeyEvent.Space || event.code === KeyEvent.Enter) {
            instance.hide();
          }
        };
        attachToElement.addEventListener('keydown', onKeyDown);
        return () => {
          attachToElement.setAttribute('title', instance.props.content as string);
          attachToElement.removeEventListener('keydown', onKeyDown);
          instance.destroy();
        };
      } catch (e) {
        windowLogger.warn(`could not create tippy for element ${attachTo}`, e);
      }
    }
  }, [attachTo, hideOnClick, hideTooltipFromScreenReader, placement]);

  return <>{children}</>;
}
